/**
 * MobileScanReceiver — ricevitore GLOBALE delle richieste di scansione sul telefono.
 *
 * Montato al root dell'app: si aggancia alla connessione WebSocket condivisa già
 * autenticata come "mobile" dalla pagina corrente (Fred/mobile-officina, mobile-operator, …).
 * Sul desktop resta inerte (il backend invia `scan_request` solo ai client mobile).
 *
 * Decoder: @zxing/library BrowserMultiFormatReader (ZXing, full-resolution) → legge QR E
 * barcode 1D (EAN/UPC/Code128/39/93/ITF/Codabar) su TUTTI i browser, iOS/Safari incluso.
 * (html5-qrcode sotto-campionava il frame e perdeva le barre fini dei barcode 1D; BarcodeDetector
 * non esiste su iOS né su Chrome desktop.)
 *
 * Flusso: scan_request → Accetta → camera + scan continuo (dedup 2s) → scan_result per ogni
 * codice → "Fine"/scan_stopped → stop.
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BrowserMultiFormatReader, DecodeHintType, BarcodeFormat,
  BinaryBitmap, HybridBinarizer, RGBLuminanceSource,
} from '@zxing/library';
import { registerMessageHandler, sendSharedMessage, ensureSharedConnected, useSharedWebSocket } from '../../../hooks/useSharedWebSocket';

interface PendingReq { requestId: string; from: string; }
interface ScanningState { requestId: string; }

const SCAN_HINTS = new Map<DecodeHintType, unknown>();
SCAN_HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
  BarcodeFormat.ITF, BarcodeFormat.CODABAR,
  BarcodeFormat.DATA_MATRIX,
]);
SCAN_HINTS.set(DecodeHintType.TRY_HARDER, true);

/**
 * FIX BUG @zxing/library 0.20: HTMLCanvasElementLuminanceSource.toGrayscaleBuffer INVERTE la
 * luminanza a chiamate alterne (static FRAME_INDEX, la 1ª chiamata è quella invertita). Con
 * TRY_HARDER attivo il reader 1D, fallito il frame invertito, chiama rotateCounterClockwise()
 * che ri-esegue toGrayscaleBuffer nello stesso frame → la parità non si ristabilisce MAI e
 * OGNI frame video viene decodificato invertito → NotFound perpetuo (né QR né barcode 1D).
 * Bypass: calcoliamo noi la luminanza (mai invertita) e usiamo RGBLuminanceSource, che non
 * supporta la rotazione via canvas e quindi non ha stato globale nascosto.
 * Verificato in isolamento: con questa sorgente MultiFormatReader + SCAN_HINTS decodifica
 * sia QR sia Code39/1D al primo tentativo, in Node e in Chrome reale.
 */
class FixedLuminanceMultiFormatReader extends BrowserMultiFormatReader {
  public override createBinaryBitmap(mediaElement: HTMLVideoElement | HTMLImageElement): BinaryBitmap {
    const ctx = this.getCaptureCanvasContext(mediaElement);
    if (mediaElement instanceof HTMLVideoElement) this.drawFrameOnCanvas(mediaElement);
    else this.drawImageOnCanvas(mediaElement);
    const canvas = this.getCaptureCanvas(mediaElement);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gray = new Uint8ClampedArray(width * height);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
      // Luminanza ITU-R: 0.299R + 0.587G + 0.114B (interi: (306R+601G+117B+512)>>10)
      gray[j] = (306 * data[i] + 601 * data[i + 1] + 117 * data[i + 2] + 0x200) >> 10;
    }
    return new BinaryBitmap(new HybridBinarizer(new RGBLuminanceSource(gray, width, height)));
  }
}

// Connettore mobile: montato SOLO su Fred (/mobile-officina) con una sessione operatore presente.
function MobileScanConnector({ userId, userName }: { userId: string; userName: string }) {
  useSharedWebSocket({ userId, userName, userType: 'mobile', enabled: true });
  return null;
}

export default function MobileScanReceiver() {
  const { pathname } = useLocation();
  const onDedicatedPage = pathname.startsWith('/remote-scan');
  const inOfficina = pathname.startsWith('/mobile-officina');
  const officina = useMemo(() => {
    if (!inOfficina) return null;
    try {
      const raw = localStorage.getItem('mobile_officina_data');
      const op = raw ? JSON.parse(raw) : null;
      return op && op.id != null ? { id: String(op.id), name: op.displayName || 'Operatore' } : null;
    } catch { return null; }
  }, [inOfficina, pathname]);

  const [pendingReq, setPendingReq] = useState<PendingReq | null>(null);
  const [scanning, setScanning] = useState<ScanningState | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const audioRef = useRef<AudioContext | null>(null);

  const beep = useCallback(() => {
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      if (!audioRef.current) audioRef.current = new AC();
      const ctx = audioRef.current;
      if (!ctx) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.16);
    } catch { /* audio non disponibile */ }
  }, []);

  const triggerFeedback = useCallback(() => {
    setShowFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setShowFlash(false), 700);
  }, []);

  // Invio AFFIDABILE: se il socket è morto (standby/cambio rete) sendSharedMessage ritorna false
  // in silenzio → forza riconnessione e riprova; feedback verde SOLO a invio riuscito.
  const trySend = useCallback((msg: Record<string, unknown>, retries: number, onOk?: () => void, onFail?: () => void) => {
    if (sendSharedMessage(msg)) { onOk?.(); return; }
    ensureSharedConnected();
    if (retries > 0) setTimeout(() => trySend(msg, retries - 1, onOk, onFail), 1200);
    else onFail?.();
  }, []);

  const lastByCode = useRef<Map<string, number>>(new Map());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const scanningRef = useRef<ScanningState | null>(null);
  useEffect(() => { scanningRef.current = scanning; }, [scanning]);

  const stopScan = useCallback(() => {
    try { readerRef.current?.reset(); } catch { /* già fermo */ }
    readerRef.current = null;
    lastByCode.current.clear();
    const v = videoRef.current;
    if (v && v.srcObject) {
      try { (v.srcObject as MediaStream).getTracks?.().forEach(t => t.stop()); } catch { /* noop */ }
      v.srcObject = null;
    }
  }, []);

  const emitRef = useRef<(code: string) => void>(() => {});

  const startScan = useCallback(async (requestId: string) => {
    setCameraError(null);
    try { readerRef.current?.reset(); } catch { /* noop */ }
    readerRef.current = null;
    const video = videoRef.current;
    if (!video) { setCameraError('Elemento video non pronto'); scanningRef.current = null; setScanning(null); return; }

    const emit = (code: string) => {
      const now = performance.now();
      const prev = lastByCode.current.get(code) || 0;
      if (now - prev < 2000) return; // dedup 2s (stesso codice)
      lastByCode.current.set(code, now);
      trySend({ type: 'scan_result', requestId, code }, 4, () => {
        navigator.vibrate?.([60, 40, 90]);
        beep();
        triggerFeedback();
        setLastCode(code);
        setScanCount(c => c + 1);
        setSendError(null);
      }, () => {
        navigator.vibrate?.(300);
        lastByCode.current.delete(code);
        setSendError('⚠️ Connessione persa: ULTIMO CODICE NON INVIATO. Riconnessione in corso, inquadra di nuovo il codice.');
      });
    };
    emitRef.current = emit;

    // Metodo standard @zxing: decodeFromConstraints gestisce camera + play + loop di decodifica con
    // il timing corretto del listener 'playing' (gestirlo a mano rompeva su PC: il play partiva
    // prima che @zxing agganciasse il listener → decode mai avviato). Alta risoluzione = barre 1D
    // più nitide. Il fix luminanza è dentro FixedLuminanceMultiFormatReader (createBinaryBitmap).
    const reader = new FixedLuminanceMultiFormatReader(SCAN_HINTS, 100);
    readerRef.current = reader;
    try {
      await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
        video,
        (result) => { if (result) emitRef.current(result.getText()); },
      );
    } catch (err: any) {
      setCameraError('Impossibile accedere alla fotocamera: ' + (err?.message || err));
      try { reader.reset(); } catch { /* noop */ }
      readerRef.current = null; scanningRef.current = null; setScanning(null);
      return;
    }
    // Sessione chiusa durante l'avvio → ferma.
    if (scanningRef.current?.requestId !== requestId) { try { reader.reset(); } catch { /* noop */ } readerRef.current = null; return; }
    // Autofocus continuo best-effort DOPO l'avvio (aiuta i barcode 1D; i QR tollerano la sfocatura).
    try {
      const track = (video.srcObject as MediaStream | null)?.getVideoTracks?.()[0];
      const caps: any = track?.getCapabilities?.() || {};
      if (track && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] });
      }
    } catch { /* focus non supportato: ignora */ }
  }, [beep, triggerFeedback, trySend]);

  // Handler WS globali (standalone: si agganciano alla connessione mobile attiva).
  useEffect(() => {
    if (onDedicatedPage) return; // /remote-scan gestisce da sé
    const un1 = registerMessageHandler('scan_request', (d: any) => {
      if (scanningRef.current) return;
      setPendingReq({ requestId: d.requestId, from: d.from || 'Desktop' });
    });
    const un2 = registerMessageHandler('scan_taken', (d: any) => {
      setPendingReq(prev => (prev && prev.requestId === d.requestId ? null : prev));
    });
    const un3 = registerMessageHandler('scan_stopped', (d: any) => {
      if (scanningRef.current && scanningRef.current.requestId === d.requestId) {
        scanningRef.current = null; stopScan(); setScanning(null);
      }
      setPendingReq(prev => (prev && prev.requestId === d.requestId ? null : prev));
    });
    return () => { un1(); un2(); un3(); };
  }, [stopScan, onDedicatedPage]);

  useEffect(() => () => {
    stopScan();
    if (flashTimer.current) clearTimeout(flashTimer.current);
    audioRef.current?.close?.().catch?.(() => {});
  }, [stopScan]);

  // Richieste scan da PUSH (app in background/chiusa, iOS incluso): il service worker fa postMessage
  // se l'app è già aperta, oppure lascia la richiesta in cache 'notification-data' e apre l'app (che
  // al boot la legge). Così lo scan raggiunge l'operatore anche senza WS/foreground.
  useEffect(() => {
    if (onDedicatedPage) return;
    const showFromPush = (data: any) => {
      if (!data?.requestId || scanningRef.current) return;
      setPendingReq({ requestId: data.requestId, from: data.fromUserName || 'Desktop' });
    };
    const onSwMsg = (ev: MessageEvent) => {
      if (ev.data?.type === 'SCAN_REQUEST_PUSH') showFromPush(ev.data.data);
    };
    navigator.serviceWorker?.addEventListener('message', onSwMsg);
    (async () => {
      try {
        const cache = await caches.open('notification-data');
        const KEY = '/pending-scan-requests';
        const res = await cache.match(KEY);
        if (!res) return;
        await cache.delete(KEY);
        const arr = await res.json();
        const recent = (Array.isArray(arr) ? arr : []).filter((r: any) => Date.now() - (r._cachedAt || 0) < 5 * 60 * 1000);
        const last = recent[recent.length - 1];
        if (last) showFromPush(last);
      } catch { /* cache non disponibile */ }
    })();
    return () => navigator.serviceWorker?.removeEventListener('message', onSwMsg);
  }, [onDedicatedPage]);

  const handleAccept = async () => {
    if (!pendingReq) return;
    const requestId = pendingReq.requestId;
    trySend({ type: 'scan_accept', requestId }, 6, () => setSendError(null), () => {
      setSendError('⚠️ Impossibile contattare il server: la scansione potrebbe non arrivare al PC. Controlla la connessione.');
    });
    setPendingReq(null);
    setSendError(null);
    scanningRef.current = { requestId };
    setScanning({ requestId });
    setScanCount(0);
    setLastCode(null);
    await startScan(requestId);
  };
  const handleReject = () => setPendingReq(null);
  const handleFine = () => {
    if (scanning) trySend({ type: 'scan_stop', requestId: scanning.requestId }, 3);
    scanningRef.current = null;
    stopScan();
    setScanning(null);
    setSendError(null);
  };

  if (onDedicatedPage) return null;
  return (
    <>
      {officina && <MobileScanConnector userId={officina.id} userName={officina.name} />}

      {pendingReq && !scanning && (
        <div style={ov.reqWrap}>
          <div style={ov.reqCard}>
            <div style={ov.reqTitle}>📷 Richiesta scansione da {pendingReq.from}</div>
            <div style={ov.reqActions}>
              <button style={{ ...ov.btn, ...ov.btnAccept }} onClick={handleAccept}>Accetta</button>
              <button style={{ ...ov.btn, ...ov.btnReject }} onClick={handleReject}>Rifiuta</button>
            </div>
          </div>
        </div>
      )}

      {/* Scansione attiva — overlay a schermo intero (sempre in DOM, toggle display). */}
      <div style={{ ...ov.scanWrap, display: scanning ? 'flex' : 'none' }}>
        <div style={ov.videoBox}>
          <video ref={videoRef} playsInline muted autoPlay style={ov.video} />
          <div style={{
            ...ov.reticle,
            borderColor: showFlash ? '#22c55e' : 'rgba(34,197,94,.85)',
            borderWidth: showFlash ? 5 : 3,
            boxShadow: showFlash ? '0 0 0 100vmax rgba(34,197,94,.30)' : '0 0 0 100vmax rgba(0,0,0,.30)',
          }} />
          {showFlash && <div style={ov.flashBadge}>✓ Letto e inviato{lastCode ? `: ${lastCode}` : ''}</div>}
        </div>
        <div style={ov.scanBar}>
          <div style={ov.scanStatus}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#22c55e' }}>{scanCount}</span>&nbsp; codici inviati
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>Barcode: tienilo <b>dritto</b> e riempi il riquadro • QR: in qualsiasi angolo</div>
          {lastCode && <div style={ov.lastCode}>Ultimo: {lastCode}</div>}
          {cameraError && <div style={ov.error}>{cameraError}</div>}
          {sendError && <div style={ov.sendError}>{sendError}</div>}
          <button style={{ ...ov.btn, ...ov.btnStop }} onClick={handleFine}>Fine scansione</button>
        </div>
      </div>
    </>
  );
}

const ov: Record<string, React.CSSProperties> = {
  reqWrap: { position: 'fixed', top: 8, left: 8, right: 8, zIndex: 2147483600, display: 'flex', justifyContent: 'center' },
  reqCard: { width: '100%', maxWidth: 420, background: '#0f172a', color: '#e2e8f0', border: '1px solid #0ea5e9', borderRadius: 14, padding: 14, boxShadow: '0 10px 30px rgba(0,0,0,.5)' },
  reqTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12 },
  reqActions: { display: 'flex', gap: 10 },
  scanWrap: { position: 'fixed', inset: 0, zIndex: 2147483600, background: '#000', display: 'flex', flexDirection: 'column' },
  videoBox: { position: 'relative', flex: 1, overflow: 'hidden', background: '#000' },
  video: { width: '100%', height: '100%', objectFit: 'cover' },
  reticle: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '86%', height: '34%', border: '3px solid rgba(34,197,94,.85)', borderRadius: 12, boxShadow: '0 0 0 100vmax rgba(0,0,0,.30)', pointerEvents: 'none' },
  scanBar: { padding: 14, background: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 },
  scanStatus: { fontSize: 14, textAlign: 'center', wordBreak: 'break-all' },
  error: { color: '#fca5a5', fontSize: 13, textAlign: 'center' },
  sendError: { background: '#7f1d1d', color: '#fecaca', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' },
  flashBadge: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(34,197,94,.95)', color: '#052e16', fontWeight: 800, fontSize: 18, padding: '12px 18px', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,.4)', maxWidth: '86%', textAlign: 'center', wordBreak: 'break-all', pointerEvents: 'none', zIndex: 5 },
  lastCode: { background: '#0b3d2e', color: '#86efac', borderRadius: 10, padding: '8px 10px', fontSize: 14, wordBreak: 'break-all', textAlign: 'center' },
  btn: { flex: 1, padding: 14, fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 12, cursor: 'pointer', color: '#fff' },
  btnAccept: { background: '#22c55e' },
  btnReject: { background: '#475569' },
  btnStop: { background: '#ef4444', width: '100%' },
};
